import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, FileText, Mail, Calendar, ClipboardList, Plus, Trash2, Pencil, X, Receipt, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";
import { BLOG_CATEGORIES, LOGO_URL } from "@/lib/constants";
import InvoicesPanel from "@/components/admin/InvoicesPanel";
import ProjectBriefsPanel from "@/components/admin/ProjectBriefsPanel";
import ProjectsPanel from "@/components/admin/ProjectsPanel";

const empty = { title: "", slug: "", excerpt: "", content: "", category: BLOG_CATEGORIES[0], cover_image: "", published: true };

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(null);
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [diags, setDiags] = useState([]);
  const [appts, setAppts] = useState([]);
  const [edit, setEdit] = useState(null); // post being edited or null
  const [form, setForm] = useState(empty);
  const nav = useNavigate();

  useEffect(() => {
    const t = localStorage.getItem("pn_token");
    if (!t) { nav("/admin/login"); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem("pn_token");
      nav("/admin/login");
    });
  }, [nav]);

  useEffect(() => { if (authed) loadAll(); }, [authed]);

  const loadAll = async () => {
    try {
      const [p, c, d, a] = await Promise.all([
        api.get("/admin/blog/posts"),
        api.get("/admin/contacts"),
        api.get("/admin/diagnostics"),
        api.get("/admin/appointments"),
      ]);
      setPosts(p.data); setContacts(c.data); setDiags(d.data); setAppts(a.data);
    } catch { toast.error("Erreur chargement"); }
  };

  const logout = () => {
    localStorage.removeItem("pn_token");
    nav("/admin/login");
  };

  const openNew = () => { setForm(empty); setEdit("new"); };
  const openEdit = (p) => { setForm(p); setEdit(p.id); };
  const closeEdit = () => { setEdit(null); setForm(empty); };

  const savePost = async () => {
    if (!form.title || !form.slug || !form.content) { toast.error("Titre, slug et contenu requis"); return; }
    try {
      if (edit === "new") {
        await api.post("/admin/blog/posts", form);
        toast.success("Article créé");
      } else {
        await api.put(`/admin/blog/posts/${edit}`, form);
        toast.success("Article mis à jour");
      }
      closeEdit();
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/blog/posts/${id}`);
    toast.success("Supprimé");
    loadAll();
  };

  if (!authed) return null;

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="" className="h-9 w-9 object-contain" />
            <span className="font-heading font-bold text-[#0B1F3A]">Admin · Passerelle</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#4B5563]">{localStorage.getItem("pn_email")}</span>
            <Button onClick={logout} variant="outline" size="sm" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-1" />Déconnexion</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 md:px-10 py-8">
        <h1 className="font-heading font-extrabold text-3xl text-[#0B1F3A] mb-2">Tableau de bord</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Articles", value: posts.length, icon: FileText },
            { label: "Contacts", value: contacts.length, icon: Mail },
            { label: "Diagnostics", value: diags.length, icon: ClipboardList },
            { label: "Rendez-vous", value: appts.length, icon: Calendar },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-5 border border-gray-200" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <s.icon className="h-5 w-5 text-[#00B8D9]" />
              </div>
              <div className="text-3xl font-heading font-extrabold text-[#0B1F3A] mt-2">{s.value}</div>
              <div className="text-sm text-[#4B5563]">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="blog">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projets</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Factures</TabsTrigger>
            <TabsTrigger value="briefs" data-testid="tab-briefs">Cahiers des charges</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="diagnostics" data-testid="tab-diagnostics">Diagnostics</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">Rendez-vous</TabsTrigger>
          </TabsList>

          <TabsContent value="blog" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">Articles</h2>
              <Button onClick={openNew} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="blog-new"><Plus className="h-4 w-4 mr-1" />Nouvel article</Button>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F7FA] text-left">
                  <tr><th className="p-3">Titre</th><th className="p-3">Catégorie</th><th className="p-3">Publié</th><th className="p-3">Date</th><th className="p-3"></th></tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="p-3 font-medium text-[#0B1F3A]">{p.title}</td>
                      <td className="p-3 text-[#4B5563]">{p.category}</td>
                      <td className="p-3">{p.published ? <span className="text-green-600 text-xs font-semibold">✓ Oui</span> : <span className="text-gray-400 text-xs">Brouillon</span>}</td>
                      <td className="p-3 text-[#4B5563]">{new Date(p.created_at).toLocaleDateString("fr-CA")}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.slug}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deletePost(p.id)} data-testid={`blog-delete-${p.slug}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <InvoicesPanel />
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <ProjectsPanel />
          </TabsContent>

          <TabsContent value="briefs" className="mt-6">
            <ProjectBriefsPanel />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6"><DataTable rows={contacts} columns={[
              { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleDateString("fr-CA") },
              { key: "nom", label: "Nom" },
              { key: "entreprise", label: "Entreprise" },
              { key: "courriel", label: "Courriel" },
              { key: "telephone", label: "Tél." },
              { key: "message", label: "Message", fmt: (v) => v?.slice(0, 80) + (v?.length > 80 ? "…" : "") },
            ]} />
          </TabsContent>

          <TabsContent value="diagnostics" className="mt-6">
            <DataTable rows={diags} columns={[
              { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleDateString("fr-CA") },
              { key: "nom", label: "Nom" },
              { key: "courriel", label: "Courriel" },
              { key: "secteur", label: "Secteur" },
              { key: "employes", label: "Employés" },
              { key: "score", label: "Score", fmt: (v) => <b>{v}/100</b> },
            ]} />
          </TabsContent>

          <TabsContent value="appointments" className="mt-6">
            <DataTable rows={appts} columns={[
              { key: "created_at", label: "Reçu", fmt: (v) => new Date(v).toLocaleDateString("fr-CA") },
              { key: "type_rdv", label: "Type" },
              { key: "date", label: "Date" },
              { key: "heure", label: "Heure" },
              { key: "nom", label: "Nom" },
              { key: "courriel", label: "Courriel" },
              { key: "telephone", label: "Tél." },
            ]} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!edit} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{edit === "new" ? "Nouvel article" : "Éditer l'article"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} data-testid="post-form-title" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="post-form-slug" /></div>
            <div><Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="post-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{BLOG_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Image de couverture (URL)</Label><Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="post-form-cover" /></div>
            <div><Label>Extrait</Label><Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="post-form-excerpt" /></div>
            <div><Label>Contenu (Markdown)</Label><Textarea rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="post-form-content" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="post-form-published" /><Label>Publié</Label></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={closeEdit}>Annuler</Button>
              <Button onClick={savePost} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="post-form-save">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DataTable({ rows, columns }) {
  if (rows.length === 0) return <div className="bg-white p-8 text-center text-[#4B5563] rounded-xl border border-gray-200">Aucune donnée.</div>;
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F5F7FA] text-left">
          <tr>{columns.map((c) => <th key={c.key} className="p-3 font-semibold text-[#0B1F3A]">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-gray-100">
              {columns.map((c) => <td key={c.key} className="p-3 text-[#4B5563]">{c.fmt ? c.fmt(r[c.key]) : (r[c.key] || "-")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
